import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Button } from "./ui/button";
import { BACKEND_BASE_URL } from "../config/api.config";

// Initialize Stripe
let stripePromise = null;

const PaymentForm = ({
  clientEmail,
  clientName,
  paymentOption,
  onSuccess,
  onError,
  onConfirmBooking,
  submitting,
  adjustedAmount,
  disabled,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !privacyAccepted) {
      return;
    }

    setProcessing(true);

    try {
      // Submit the payment element
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      if (paymentOption === "payAtClinic") {
        // Pay at Clinic: Just save the card (existing flow)
        const setupResult = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: "if_required",
        });

        const resolvedSetupIntent =
          setupResult.setupIntent ||
          (setupResult.error &&
          setupResult.error.code === "setup_intent_unexpected_state" &&
          setupResult.error.setup_intent?.status === "succeeded"
            ? setupResult.error.setup_intent
            : null);

        if (setupResult.error && !resolvedSetupIntent) {
          throw new Error(setupResult.error.message);
        }

        if (resolvedSetupIntent && resolvedSetupIntent.status === "succeeded") {
          // Call backend to save payment method
          const response = await fetch(
            `${BACKEND_BASE_URL}/api/payment/save-method`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                paymentMethodId: resolvedSetupIntent.payment_method,
                clientEmail,
                clientName,
              }),
            },
          );

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || "Failed to save payment method");
          }

          const data = await response.json();
          setPaymentCompleted(true);
          onSuccess({
            customerId: data.customerId,
            paymentMethodId: data.paymentMethodId,
            privacyPolicyAccepted: privacyAccepted,
            paymentOption: "payAtClinic",
          });
        }
      } else if (paymentOption === "payUpfront") {
        // Pay Upfront: First save the card, then charge it immediately
        // Step 1: Save the card using SetupIntent (same as Pay at Clinic)
        const setupResult = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: "if_required",
        });

        const resolvedSetupIntent =
          setupResult.setupIntent ||
          (setupResult.error &&
          setupResult.error.code === "setup_intent_unexpected_state" &&
          setupResult.error.setup_intent?.status === "succeeded"
            ? setupResult.error.setup_intent
            : null);

        if (setupResult.error && !resolvedSetupIntent) {
          throw new Error(setupResult.error.message);
        }

        if (resolvedSetupIntent && resolvedSetupIntent.status === "succeeded") {
          // Step 2: Save payment method to backend
          const saveResponse = await fetch(
            `${BACKEND_BASE_URL}/api/payment/save-method`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                paymentMethodId: resolvedSetupIntent.payment_method,
                clientEmail,
                clientName,
              }),
            },
          );

          if (!saveResponse.ok) {
            const data = await saveResponse.json();
            throw new Error(data.message || "Failed to save payment method");
          }

          const saveData = await saveResponse.json();

          // Step 3: Create and charge the payment intent immediately
          // Use adjustedAmount if provided (includes gift card discount), otherwise default to $100
          const chargeAmount = adjustedAmount || 10000; // $100 CAD in cents default
          const chargeResponse = await fetch(
            `${BACKEND_BASE_URL}/api/payment/charge-upfront`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                customerId: saveData.customerId,
                paymentMethodId: saveData.paymentMethodId,
                amount: chargeAmount,
                clientEmail,
              }),
            },
          );

          if (!chargeResponse.ok) {
            const data = await chargeResponse.json();
            throw new Error(data.message || "Failed to process payment");
          }

          const chargeData = await chargeResponse.json();

          setPaymentCompleted(true);
          onSuccess({
            customerId: saveData.customerId,
            paymentMethodId: saveData.paymentMethodId,
            privacyPolicyAccepted: privacyAccepted,
            paymentOption: "payUpfront",
            paymentIntentId: chargeData.chargeId,
            charged: true,
          });
        }
      }
    } catch (err) {
      onError(err.message || "Failed to process payment");
    } finally {
      setProcessing(false);
    }
  };

  // Dynamic button text based on payment option
  const getButtonText = () => {
    if (paymentCompleted && submitting) {
      return "Confirming Booking...";
    }
    if (processing) {
      return paymentOption === "payUpfront"
        ? "Processing Payment..."
        : "Securing Card...";
    }
    if (paymentCompleted) {
      return paymentOption === "payUpfront"
        ? "Payment Complete - Confirm Booking"
        : "Card Saved - Confirm Booking";
    }
    return paymentOption === "payUpfront"
      ? "Pay & Confirm Booking"
      : "Save Card & Confirm Booking";
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Important Notice */}
      <div
        className={`rounded-xl border p-4 ${
          paymentOption === "payUpfront"
            ? "bg-blue-50 border-blue-200"
            : "bg-emerald-50 border-emerald-200"
        }`}>
        <div className="flex items-start gap-3">
          <div
            className={`text-xl ${
              paymentOption === "payUpfront"
                ? "text-blue-600"
                : "text-emerald-600"
            }`}>
            {paymentOption === "payUpfront" ? "💳" : "🔒"}
          </div>
          <div className="flex-1">
            <h3
              className={`font-semibold text-sm mb-1 ${
                paymentOption === "payUpfront"
                  ? "text-blue-900"
                  : "text-emerald-900"
              }`}>
              {paymentOption === "payUpfront"
                ? "Instant Payment & Receipt"
                : "Secure Card on File"}
            </h3>
            <p
              className={`text-sm ${
                paymentOption === "payUpfront"
                  ? "text-blue-800"
                  : "text-emerald-800"
              }`}>
              {paymentOption === "payUpfront" ? (
                "Your card will be charged immediately and you'll receive a receipt for your booking. You will get an invoice for your appointment after the appointment is completed."
              ) : (
                <>
                  <strong>No charge will be made at this time.</strong> We're
                  only securely saving your payment method for your booking.
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Payment Element */}
      <div className="rounded-xl border border-gray-200 p-4 bg-white">
        <PaymentElement />
      </div>

      {/* Privacy Policy & Cancellation Policy */}
      <div className="rounded-xl border border-gray-200 p-4 bg-white space-y-3">
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <div className="flex-1 text-sm">
            <p className="text-gray-900 font-medium mb-2">
              I accept the cancellation and privacy policies
            </p>
            <div className="text-gray-600 space-y-1 text-xs leading-relaxed">
              <p>
                <strong>📅 Cancellation Policy (2-hour window):</strong> You may
                cancel your appointment up to 2 hours before your scheduled time
                with no charge. Cancellations made less than 2 hours before or
                no-shows will be charged the full session fee.
              </p>
              <p className="mt-2">
                <strong>🔒 Privacy Policy:</strong> Your payment information is
                securely stored and encrypted. We will only charge your card in
                the event of a late cancellation (less than 2 hours) or no-show.
                Your data is protected and will not be shared with third
                parties.
              </p>
            </div>
          </div>
        </label>
      </div>

      {/* Success Message when payment is completed */}
      {paymentCompleted && (
        <div
          className={`rounded-xl border-2 p-4 ${
            paymentOption === "payUpfront"
              ? "bg-blue-50 border-blue-300"
              : "bg-emerald-50 border-emerald-300"
          }`}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">✅</span>
            <div className="flex-1">
              <div className="font-semibold text-gray-900">
                {paymentOption === "payUpfront"
                  ? "Payment Successful!"
                  : "Card Saved Successfully!"}
              </div>
              <div className="text-sm text-gray-700">
                {paymentOption === "payUpfront"
                  ? "Your payment has been processed. Click below to confirm your booking."
                  : "No charge has been made. Click below to confirm your booking."}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Combined Button */}
      <Button
        type={paymentCompleted ? "button" : "submit"}
        onClick={paymentCompleted ? onConfirmBooking : undefined}
        disabled={!stripe || processing || !privacyAccepted || submitting || disabled}
        className="w-full">
        {getButtonText()}
      </Button>

      {!privacyAccepted && (
        <p className="text-xs text-center text-amber-700">
          Please accept the cancellation and privacy policies to continue
        </p>
      )}
    </form>
  );
};

export default function PaymentMethodCollection({
  clientEmail,
  clientName,
  paymentOption,
  onSuccess,
  onError,
  onConfirmBooking,
  submitting,
  adjustedAmount, // Adjusted amount in cents after gift card discount
  disabled,
}) {
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [publishableKey, setPublishableKey] = useState("");

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        console.log("🔄 Initializing Stripe payment system...", paymentOption);

        // Get publishable key
        const keyRes = await fetch(
          `${BACKEND_BASE_URL}/api/payment/publishable-key`,
        );

        if (!keyRes.ok) {
          const errorData = await keyRes.json();
          throw new Error(errorData.message || "Failed to get Stripe key");
        }

        const keyData = await keyRes.json();
        console.log(
          "✅ Publishable key received:",
          keyData.publishableKey ? "Yes" : "No",
        );

        if (!keyData.publishableKey) {
          throw new Error(
            "Stripe publishable key not configured on server. Please restart the backend server.",
          );
        }

        setPublishableKey(keyData.publishableKey);

        // Initialize Stripe
        if (!stripePromise && keyData.publishableKey) {
          console.log("🔄 Loading Stripe.js...");
          stripePromise = loadStripe(keyData.publishableKey);
        }

        // Always create setup intent for card collection
        // For Pay Upfront, we'll charge separately after collecting the card
        console.log("🔄 Creating setup intent...");
        const response = await fetch(
          `${BACKEND_BASE_URL}/api/payment/setup-intent`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ clientEmail, clientName }),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to initialize payment");
        }

        const data = await response.json();
        console.log("✅ Setup intent created successfully");
        setClientSecret(data.clientSecret);
      } catch (error) {
        console.error("❌ Payment initialization error:", error);
        onError(error.message || "Failed to initialize payment");
      } finally {
        setLoading(false);
      }
    };

    if (clientEmail) {
      setLoading(true);
      initializeStripe();
    }
  }, [clientEmail, clientName, paymentOption, onError]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="h-8 w-8 mx-auto mb-3 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600"></div>
          <p className="text-sm text-gray-600">
            Initializing secure payment...
          </p>
        </div>
      </div>
    );
  }

  if (!clientSecret || !publishableKey) {
    return (
      <div className="text-center py-8 text-red-600">
        Failed to initialize payment system
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: "#059669",
        colorBackground: "#ffffff",
        colorText: "#1f2937",
        colorDanger: "#dc2626",
        fontFamily: "system-ui, sans-serif",
        borderRadius: "12px",
      },
    },
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Payment Information
        </h3>
        <p className="text-sm text-gray-600">
          Secure your booking with a card on file
        </p>
      </div>

      <Elements stripe={stripePromise} options={options}>
        <PaymentForm
          clientEmail={clientEmail}
          clientName={clientName}
          paymentOption={paymentOption}
          onSuccess={onSuccess}
          onError={onError}
          onConfirmBooking={onConfirmBooking}
          submitting={submitting}
          adjustedAmount={adjustedAmount}
          disabled={disabled}
        />
      </Elements>
    </div>
  );
}
